import { apiClient } from './client'
import type {
  PerformanceListParams,
  PerformanceListResult,
  PerformanceRankingRow,
  PerformanceSummary,
  EmployeeReportResult,
  UnitReportResult,
  PerformanceDetailItem,
  PerformanceDashboard,
} from '@/types/performance'

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== ''

const buildSearch = (params: PerformanceListParams) => {
  const search = new URLSearchParams()
  search.set('page', String(params.page || 1))
  search.set('page_size', String(params.page_size || 30))
  if (hasValue(params.user_id)) search.set('user_id', String(params.user_id))
  if (hasValue(params.start_date)) search.set('start_date', params.start_date!)
  if (hasValue(params.end_date)) search.set('end_date', params.end_date!)
  if (hasValue(params.order_no)) search.set('order_no', params.order_no!)
  return search.toString()
}

export const performanceApi = {
  getList: async (params: PerformanceListParams): Promise<PerformanceListResult> => {
    const response = await apiClient.get<PerformanceListResult>(
      `/performances?${buildSearch(params)}`
    )
    return response.data
  },

  getRankings: async (params: {
    start_date?: string
    end_date?: string
    dimension?: string
  }): Promise<PerformanceRankingRow[]> => {
    const search = new URLSearchParams()
    if (hasValue(params.start_date)) search.set('start_date', params.start_date!)
    if (hasValue(params.end_date)) search.set('end_date', params.end_date!)
    if (hasValue(params.dimension)) search.set('dimension', params.dimension!)
    const qs = search.toString()
    const response = await apiClient.get<PerformanceRankingRow[]>(
      `/performances/ranking${qs ? `?${qs}` : ''}`
    )
    return response.data
  },

  getSummary: async (params: {
    user_id?: number
    start_date?: string
    end_date?: string
  }): Promise<PerformanceSummary> => {
    const search = new URLSearchParams()
    if (hasValue(params.user_id)) search.set('user_id', String(params.user_id))
    if (hasValue(params.start_date)) search.set('start_date', params.start_date!)
    if (hasValue(params.end_date)) search.set('end_date', params.end_date!)
    const qs = search.toString()
    const response = await apiClient.get<PerformanceSummary>(
      `/performances/summary${qs ? `?${qs}` : ''}`
    )
    return response.data
  },

  getEmployeeReport: async (params: {
    start_date?: string
    end_date?: string
    department_id?: number
    user_id?: number
  }): Promise<EmployeeReportResult> => {
    const search = new URLSearchParams()
    if (hasValue(params.start_date)) search.set('start_date', params.start_date!)
    if (hasValue(params.end_date)) search.set('end_date', params.end_date!)
    if (hasValue(params.department_id)) search.set('department_id', String(params.department_id))
    if (hasValue(params.user_id)) search.set('user_id', String(params.user_id))
    const qs = search.toString()
    const response = await apiClient.get<EmployeeReportResult>(
      `/performances/employee-report${qs ? `?${qs}` : ''}`
    )
    return response.data
  },

  getUnitReport: async (params: {
    start_date?: string
    end_date?: string
  }): Promise<UnitReportResult> => {
    const search = new URLSearchParams()
    if (hasValue(params.start_date)) search.set('start_date', params.start_date!)
    if (hasValue(params.end_date)) search.set('end_date', params.end_date!)
    const qs = search.toString()
    const response = await apiClient.get<UnitReportResult>(
      `/performances/unit-report${qs ? `?${qs}` : ''}`
    )
    return response.data
  },

  getPromotionDetail: async (params: {
    page: number
    page_size: number
    start_date?: string
    end_date?: string
    order_no?: string
    user_id?: number
    sort_by?: string
    sort_order?: string
  }): Promise<{ list: PerformanceDetailItem[]; total: number; page: number; page_size: number }> => {
    const search = new URLSearchParams()
    search.set('page', String(params.page || 1))
    search.set('page_size', String(params.page_size || 30))
    if (hasValue(params.start_date)) search.set('start_date', params.start_date!)
    if (hasValue(params.end_date)) search.set('end_date', params.end_date!)
    if (hasValue(params.order_no)) search.set('order_no', params.order_no!)
    if (hasValue(params.user_id)) search.set('user_id', String(params.user_id))
    if (hasValue(params.sort_by)) search.set('sort_by', params.sort_by!)
    if (hasValue(params.sort_order)) search.set('sort_order', params.sort_order!)
    const response = await apiClient.get<{ list: PerformanceDetailItem[]; total: number; page: number; page_size: number }>(
      `/performances/promotion-detail?${search.toString()}`
    )
    return response.data
  },

  getDashboard: async (): Promise<PerformanceDashboard> => {
    const response = await apiClient.get<PerformanceDashboard>('/performances/dashboard')
    return response.data
  },
}
